/**
 * Reusable ContinueButton component for landing page
 */

import { useNavigate } from "react-router-dom";
import { cn } from "../../lib/utils";
import { focusStyles, hoverStyles, disabledStyles } from "../../utils/styles";
import { APP_CONFIG } from "../../constants/landing";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setLoading, selectIsLoading } from "../../store/slices/appSlice";
import { Spinner } from "../ui/spinner";

interface ContinueButtonProps {
  canContinue: boolean;
  continuePath: string;
  theme: {
    components: {
      button: string;
    };
  };
}

export function ContinueButton({ canContinue, continuePath, theme }: ContinueButtonProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectIsLoading);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!canContinue || isLoading) return;

    dispatch(setLoading(true));

    // Add a small delay to show loading state
    setTimeout(() => {
      navigate(continuePath);
      // Stop loading after navigation completes
      setTimeout(() => {
        dispatch(setLoading(false));
      }, 100);
    }, 500);
  };

  return (
    <div className="text-center">
      <button
        onClick={handleClick}
        disabled={!canContinue || isLoading}
        aria-describedby={!canContinue ? "continue-disabled-reason" : undefined}
        className={cn(
          "min-h-[2rem] sm:min-h-[2.5rem] px-4 sm:px-8 py-1 sm:py-2 text-base sm:text-xl font-bold rounded-full",
          "bg-brand-primary text-text-primary border-2 border-brand-primary hover:border-brand-secondary",
          "hover:bg-brand-primary/80 hover:text-text-primary",
          "inline-flex items-center justify-center",
          focusStyles.ring,
          hoverStyles.scale,
          (!canContinue || isLoading) && disabledStyles.default
        )}
      >
        {isLoading ? (
          <>
            <Spinner variant="pinwheel" size={20} className="mr-3 text-current" />
            Loading...
          </>
        ) : (
          APP_CONFIG.continueButtonText
        )}
      </button>
      {!canContinue && (
        <p id="continue-disabled-reason" className="sr-only">
          Continue button is disabled. Please select an age range and provide consent to continue.
        </p>
      )}
    </div>
  );
}
